import * as React from 'react';
import {type VariantProps} from 'class-variance-authority';
import {cn} from '../lib/cn';
import {Tooltip, TooltipContent, TooltipTrigger} from '../tooltip';
import {iconButtonVariants} from './iconButtonVariants';

export interface IconButtonProps
  extends
    Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'children'>,
    VariantProps<typeof iconButtonVariants> {
  label?: string;
  children?: React.ReactNode;
  tooltip?: string;
  icon?: React.ReactNode;
  outlined?: boolean;
}

export const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(
  (
    {
      className,
      size,
      shape,
      variant,
      label,
      children,
      icon,
      tooltip,
      outlined,
      type = 'button',
      ...props
    },
    ref,
  ) => {
    const button = (
      <button
        ref={ref}
        type={type}
        aria-label={props['aria-label'] ?? label}
        className={cn(
          iconButtonVariants({size, shape, variant}),
          outlined &&
            'h-[30px] w-[30px] rounded-sm border border-[var(--chekin-border-default)] bg-[var(--chekin-color-white)]',
          className,
        )}
        {...props}
      >
        {icon ?? children}
      </button>
    );

    if (!tooltip) return button;

    return (
      <Tooltip>
        <TooltipTrigger asChild>{button}</TooltipTrigger>
        <TooltipContent>{tooltip}</TooltipContent>
      </Tooltip>
    );
  },
);
IconButton.displayName = 'IconButton';
